import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { MarketId, Timeframe } from "@/stores/tradeStore";
import { useTradeStore } from "@/stores/tradeStore";
import { MARKET_CONFIG, type CandleData } from "@/lib/hyperliquid";
import { marketDataApi } from "@/lib/api";
import { useHyperliquidSocket } from "./useHyperliquidSocket";

export type { CandleData };

const timeframeToDuration = (timeframe: Timeframe) => {
  switch (timeframe) {
    case "1m":
      return 360; // 6h
    case "5m":
      return 288; // 24h
    case "15m":
      return 288; // 3 days
    case "1h":
      return 240; // 10 days
    case "4h":
      return 180; // 30 days
    case "1d":
      return 180; // 6 months
    case "1w":
      return 104; // 2 years
    case "1M":
      return 60; // 5 years
    default:
      return 240;
  }
};

export const useTradeData = (market: MarketId, timeframe: Timeframe) => {
  const [data, setData] = useState<CandleData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const setLivePrices = useTradeStore((s) => s.setLivePrices);

  // Handle real-time candle from Hyperliquid WebSocket
  const handleCandleUpdate = useCallback((candle: CandleData) => {
    setData((prevData) => {
      if (prevData.length === 0) return prevData;

      const lastCandle = prevData[prevData.length - 1];

      // Same candle period — update in place
      if (lastCandle && lastCandle.time === candle.time) {
        const updated = [...prevData];
        updated[updated.length - 1] = candle;
        return updated;
      }

      // New candle period — append (trim to keep max 300)
      if (candle.time > (lastCandle?.time ?? 0)) {
        return [...prevData.slice(-299), candle];
      }

      return prevData;
    });

    // Live ticks still come from the Hyperliquid WS (only relevant for
    // crypto tenants today). SFWL-005 Phase 2 will route this through a
    // backend WebSocket fan-out so non-Hyperliquid tenants get streaming
    // updates from their provider.
    setDataSource((prev) =>
      prev?.endsWith("-ws") ? prev : `${prev ?? "hyperliquid"}-ws`
    );
  }, []);

  // Handle allMids from WebSocket — update livePrices in store
  const handleMidPrices = useCallback(
    (mids: Record<string, string>, dex?: string) => {
      const prices: Partial<Record<MarketId, number>> = {};

      for (const [marketId, config] of Object.entries(MARKET_CONFIG)) {
        // Match dex: undefined matches default perps, "cash"/"xyz" match explicitly
        const configDex = config.dex;
        if (configDex !== dex) continue;

        const mid = mids[config.coin];
        if (mid) {
          prices[marketId as MarketId] = parseFloat(mid);
        }
      }

      if (Object.keys(prices).length > 0) {
        setLivePrices(prices);
      }
    },
    [setLivePrices]
  );

  // Hyperliquid WebSocket connection
  const { isConnected, hasLiveFeed } = useHyperliquidSocket({
    market,
    timeframe,
    onCandle: handleCandleUpdate,
    onMidPrices: handleMidPrices,
    enabled: true,
  });

  // SFWL-005: Historical candles now come from the backend `/api/market-data/candles`
  // endpoint, which dispatches to the tenant-configured provider via the
  // server-side registry. The browser never holds vendor credentials.
  const fetchCandles = useCallback(async () => {
    const limit = timeframeToDuration(timeframe);

    setLoading(true);
    setError(null);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      const result = await marketDataApi.getCandles({
        symbol: market,
        interval: timeframe,
        limit,
        signal: abortControllerRef.current.signal,
      });

      setData(result.candles);
      setDataSource(result.providerId);
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;

      console.error("[useTradeData] Candle fetch error:", err);
      setError(err instanceof Error ? err.message : "Market data unavailable");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [market, timeframe]);

  // Reload function for manual refresh
  const reload = useCallback(() => {
    fetchCandles();
  }, [fetchCandles]);

  // Initial load when market or timeframe changes
  useEffect(() => {
    fetchCandles();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchCandles]);

  // Subscribe to live mid price reactively so WS updates trigger recomputation
  const livePrice = useTradeStore((s) => s.livePrices[market]);

  // Use live mid price if available, otherwise last candle close
  const lastPrice = useMemo(() => {
    if (livePrice) return livePrice;
    return data.length ? data[data.length - 1].close : 0;
  }, [data, livePrice]);

  return {
    data,
    lastPrice,
    reload,
    loading,
    error,
    dataSource,
    isConnected,
    // FEAT-003: false when `market` has no Hyperliquid coin mapping — the
    // trade view should show a "live data unavailable" indicator.
    hasLiveFeed,
    latency: null as number | null, // Hyperliquid WS doesn't expose round-trip latency
  };
};
